import React, { useEffect, useState, useCallback, useMemo, useRef, memo } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Avatar,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
  LinearProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import {
  downloadV2VSheet,
  GetMemberProfileById,
  getAchievementPinsWithProgress
} from "src/api/commonapi";
import PinReportModal from "./StockManagement/Products/pinReportModal";
import { Routes } from "../constant/api_url";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { SvgIcon } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import WcIcon from "@mui/icons-material/Wc";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import DescriptionIcon from "@mui/icons-material/Description";
import CakeIcon from "@mui/icons-material/Cake";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GavelIcon from "@mui/icons-material/Gavel";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import { notification } from "src/utils/messages";
import CustomLoader from "src/commonComponents/CustomLoader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DownloadOutlined, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
// import DownloadIcon from "@mui/icons-material/Download";
// import jsPDF from "jspdf";

const THEME_COLOR = "#1c4766";

// ---- Memoized Achievement Card (isolates heavy render) ----
const AchievementCard = memo(function AchievementCard({ item, memberProfileData, onSelect }) {
  return (
    <Grid item xs={12} sm={6}>
      <Card
        variant="outlined"
        onClick={() => {
          if (item.disable_pin === 1) return;
          onSelect({
            p_a_p_id: item.a_p_id,
            chpt_id: memberProfileData?.chpt_id,
            m_id: memberProfileData?.m_id,
            p_name: item.name,
            m_name: memberProfileData?.m_name
          });
        }}
        sx={{
          borderRadius: 2,
          p: 2,
          height: "100%",
          display: "flex",
          gap: 2,
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          cursor: item.disable_pin === 1 ? "default" : "pointer",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: item.disable_pin === 1 ? "none" : "0 4px 12px rgba(0,0,0,0.05)",
            borderColor: item.disable_pin === 1 ? "#e5e7eb" : THEME_COLOR
          }
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            bgcolor: "#f3f4f6",
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden"
          }}
        >
          <img
            src={`${Routes.API_HOSTNAME}achievement-pin-image/${item.image}`}
            alt={item.name}
            loading="lazy"
            decoding="async"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              padding: "8px"
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1c4766", fontSize: "0.95rem", lineHeight: 1.2, mb: 0.5, textTransform: "uppercase" }}>
            {item.name}
          </Typography>
          <Typography variant="caption" sx={{ color: "#6b7280", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.3, mb: 1 }}>
            {item.description}
          </Typography>
          {item.disable_pin === 1 ? (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: "#fff5f5", border: "1px solid #fee2e2", borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <LockIcon sx={{ fontSize: 16, color: "#ef4444", mt: 0.2 }} />
              <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 500, lineHeight: 1.2 }}>
                {item.disable_message}
              </Typography>
            </Box>
          ) : item.is_achieved ? (
            <Box sx={{ mt: 1 }}>
              <Chip icon={<CheckCircleIcon style={{ color: "#fff", fontSize: 16 }} />} label={item.assign_date && `Achieved on: ${item.assign_date}`} size="small" sx={{ bgcolor: "#22c55e", color: "#fff", fontWeight: 700, height: 24, "& .MuiChip-icon": { color: "#fff" } }} />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {item.progress && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
                      {item.progress.current_count} / {item.progress.required_count}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#1c4766" }}>
                      {item.progress.percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={item.progress.percentage} sx={{ height: 6, borderRadius: 3, bgcolor: "#f3f4f6", "& .MuiLinearProgress-bar": { bgcolor: item.progress.percentage >= 100 ? "#22c55e" : item.progress.percentage > 0 ? "#3b82f6" : "#d1d5db" } }} />
                </>
              )}
              {item.is_eligible && item.eligibality_date && (
                <Chip icon={<CheckCircleIcon style={{ color: "#22c55e", fontSize: 16 }} />} label={`Eligible since ${item.eligibality_date}`} size="small" variant="outlined" sx={{ mt: 1, borderColor: "#22c55e", color: "#22c55e", fontWeight: 700, height: 24, px: 0.5, "& .MuiChip-icon": { color: "#22c55e" } }} />
              )}
              {(item.amount !== undefined || item.pending_amount !== undefined) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Box sx={{ bgcolor: "#f0fdf4", px: 1, py: 0.5, borderRadius: 1, display: "flex", alignItems: "center", gap: 0.5, border: "1px solid #dcfce7" }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: "#22c55e" }} />
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#166534", fontSize: "0.65rem" }}>
                      COUNTED ₹{item.amount || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: "#fffaf5", px: 1, py: 0.5, borderRadius: 1, display: "flex", alignItems: "center", gap: 0.5, border: "1px solid #ffedd5" }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: "#f97316" }} />
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#9a3412", fontSize: "0.65rem" }}>
                      PENDING ₹{item.pending_amount || 0}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Grid>
  );
});

const AchievementsSkeleton = memo(function AchievementsSkeleton() {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={6} key={i}>
          <Card variant="outlined" sx={{ borderRadius: 2, p: 2, display: "flex", gap: 2 }}>
            <Box sx={{ width: 80, height: 80, bgcolor: "#f3f4f6", borderRadius: 1.5 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ height: 16, bgcolor: "#e5e7eb", borderRadius: 1, mb: 1, width: "70%" }} />
              <Box sx={{ height: 12, bgcolor: "#f3f4f6", borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ height: 12, bgcolor: "#f3f4f6", borderRadius: 1, mb: 1, width: "90%" }} />
              <Box sx={{ height: 6, bgcolor: "#e5e7eb", borderRadius: 3 }} />
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});


/* ===== Styled Components ===== */

const HeaderSection = styled(Box)({
  background: `linear-gradient(135deg, ${THEME_COLOR}, #256083)`,
  color: "#fff",
  padding: "28px",
  position: "relative",
  top: 0,
  zIndex: 10
});

const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M18.901 1.153h3.68l-8.043 9.19L24 22.847h-7.406l-5.8-7.594-6.64 7.594H.47l8.6-9.83L0 1.153h7.594l5.243 6.932 6.064-6.932zm-1.292 19.36h2.04L6.48 3.395H4.29l13.319 17.118z"
    />
  </SvgIcon>
);

const SectionCard = styled(Card)({
  padding: "24px",
  borderRadius: 16,
  boxShadow: "0 6px 24px rgba(0,0,0,0.06)"
});

export default function MemberProfileModal({
  memberProfileModal,
  setMemberProfileId,
  memberProfileId,
  toggleMemberProfileModal
}) {
  const genderLabel = ["Not Selected", "Male", "Female"];
  const noYesLabel = ["No", "Yes"];

  const navigate = useNavigate();

  const [memberProfileData, setMemberProfileData] = useState(null);
  const [testimonialsHistoryTracker, setTestimonialsHistoryTracker] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPinsLoading, setIsPinsLoading] = useState(false);
  const [achievementList, setAchievementList] = useState(null); // split state to avoid re-rendering whole profile
  const [openImage, setOpenImage] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const requestIdRef = useRef(0); // for race-condition guard
  // const [isPdfDownloadLoading, setIsPdfDownloadLoading] = useState(false);

  const [sectionOpen, setSectionOpen] = useState({
    personalInformation: true,
    businessInformation: true,
    socialMediaProfiles: true,
    achievements: true,
    engagementStatus: true,
    testimonials: true,
    givenTestimonials: true,
    connectedBlockedMembers: true,
    feesHistory: true,
    inductedMember: true
  });

  const [pinReportOpen, setPinReportOpen] = useState(false);
  const [selectedPinData, setSelectedPinData] = useState(null);

  // Optimized: derive social media via useMemo (no extra render cycle)
  const memberSocialMediaProfileData = useMemo(() => {
    if (!memberProfileData) return [];
    return [
      { value: memberProfileData?.linkedin, icon: <LinkedInIcon fontSize="medium" />, bg: "#0A66C2" },
      { value: memberProfileData?.instagram, icon: <InstagramIcon fontSize="medium" />, bg: "linear-gradient(45deg, #f9ce34, #ee2a7b, #6228d7)" },
      { value: memberProfileData?.fb, icon: <FacebookIcon fontSize="medium" />, bg: "#1877F2" },
      { value: memberProfileData?.twitter, icon: <XIcon fontSize="medium" />, bg: "#000" },
      { value: memberProfileData?.youtube, icon: <YouTubeIcon fontSize="medium" />, bg: "#FF0000" },
      { value: memberProfileData?.website_url, icon: <LanguageIcon fontSize="medium" />, bg: THEME_COLOR },
      {
        value: memberProfileData?.lat_id && memberProfileData?.lang_id ? `https://www.google.com/maps?q=${memberProfileData.lat_id},${memberProfileData.lang_id}` : null,
        icon: <FmdGoodIcon fontSize="medium" />,
        bg: "#34A853"
      }
    ].filter((item) => item.value);
  }, [memberProfileData]);

  // Memoized derived achievement source (supports both API field names)
  const displayAchievements = useMemo(() => {
    if (achievementList) return achievementList;
    if (memberProfileData?.achievementlist) return memberProfileData.achievementlist;
    if (memberProfileData?.achievement) return memberProfileData.achievement;
    return [];
  }, [achievementList, memberProfileData]);

  // Memoize static info to prevent array recreation on every render
  const personalInfoItems = useMemo(() => [
    { label: "Full Name", value: memberProfileData?.m_name, icon: <PersonIcon /> },
    { label: "Position", value: `${memberProfileData?.member_new_position || ""} - ${memberProfileData?.combined_director || ""}`, icon: <WorkIcon /> },
    { label: "Chapter", value: memberProfileData?.chpt_name, icon: <ApartmentIcon /> },
    { label: "Mobile Number", value: memberProfileData?.mobile_no, icon: <PhoneIcon /> },
    { label: "Email Address", value: memberProfileData?.m_email_id, icon: <EmailIcon /> },
    { label: "Gender", value: genderLabel[memberProfileData?.gender] ?? "-", icon: <WcIcon /> },
    { label: "Date of Birth", value: memberProfileData?.dob, icon: <CakeIcon /> },
    { label: "Blood Group", value: memberProfileData?.blood_group, icon: <FavoriteIcon /> },
    { label: "State", value: memberProfileData?.state_name, icon: <PlaceIcon /> },
    { label: "City", value: memberProfileData?.city_name, icon: <LocationCityIcon /> },
    { label: "Joining Date", value: memberProfileData?.joining_date, icon: <EventAvailableIcon /> },
    { label: "Expiry Date", value: memberProfileData?.due_date, icon: <EventBusyIcon /> },
    { label: "Referred By", value: memberProfileData?.member_referal_by, icon: <SupervisorAccountIcon /> },
    { label: "Privacy Policy Accepted", value: noYesLabel[memberProfileData?.m_privacy_policy_status] ?? "-", icon: <VerifiedUserIcon /> },
    { label: "Terms & Conditions Accepted", value: noYesLabel[memberProfileData?.m_term_condition_status] ?? "-", icon: <GavelIcon /> }
  ], [memberProfileData]);

  const businessInfoItems = useMemo(() => [
    { label: "Business Name", value: memberProfileData?.business_name, icon: <BusinessIcon /> },
    { label: "Business Category", value: memberProfileData?.business_category, icon: <CategoryIcon /> },
    { label: "Services / Products", value: memberProfileData?.services_products, icon: <LocalOfferIcon /> },
    { label: "Business Description", value: memberProfileData?.business_description || memberProfileData?.business_desc, icon: <DescriptionIcon /> },
    { label: "Business Since", value: memberProfileData?.business_since, icon: <CalendarTodayIcon /> },
    { label: "Business Address", value: memberProfileData?.b_address, icon: <LocationOnIcon /> },
    { label: "Business Email", value: memberProfileData?.b_email_id, icon: <EmailIcon /> },
    { label: "Business Mobile", value: memberProfileData?.b_mobile_no, icon: <PhoneIcon /> }
  ], [memberProfileData]);

  const handleToggle = useCallback((section) => {
    setSectionOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleSelectPin = useCallback((pinData) => {
    setSelectedPinData(pinData);
    setPinReportOpen(true);
  }, []);

  const GetMemberProfile = useCallback(async (id) => {
    if (!id) return;
    const currentRequestId = ++requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    setIsLoading(true);
    setAchievementList(null);
    setIsPinsLoading(false);

    try {
      const resp = await GetMemberProfileById(id);
      if (isStale()) return;
      if (resp.status == 1) {
        const data = resp?.data;
        setMemberProfileData(data);
        setIsLoading(false); // show profile immediately - don't block on pins

        if (data?.m_id && data?.chpt_id) {
          setIsPinsLoading(true);
          try {
            const pinResp = await getAchievementPinsWithProgress({ m_id: data.m_id, chpt_id: data.chpt_id });
            if (isStale()) return;
            if (pinResp.status === 1) {
              const list = pinResp.achievementlist || [];
              setAchievementList(list);
            }
          } catch (e) {
            console.error("Pins fetch error:", e);
          } finally {
            if (!isStale()) setIsPinsLoading(false);
          }
        }
      } else {
        let notify = notification({ type: resp.status == 0 ? "error" : "success", message: resp.message });
        notify();
        setIsLoading(false);
      }
    } catch (error) {
      if (!isStale()) {
        console.error(error);
        setIsLoading(false);
        setIsPinsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!memberProfileId) return;
    GetMemberProfile(memberProfileId);
    setTestimonialsHistoryTracker((prev) => (prev.length === 0 ? [memberProfileId] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberProfileId, GetMemberProfile]);

  const formatUrl = (url) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  const getV2VSheetData = async (id) => {
    navigate(`/memberv2vsheet/${id}`);
  };

  const downloadV2VSheetData = async (member_id) => {
    try {
      setDownloadingId(member_id);
      const resp = await downloadV2VSheet(member_id);

      if (resp.status === 1 && resp.filename) {
        const fileUrl = `${Routes.API_HOSTNAME}memberv2vsheet-pdf/${resp.filename}`; // Update with your actual API URL
        // Fetch the file as a blob
        const response = await fetch(fileUrl, { method: "GET" });
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        // Create an invisible <a> element to trigger download
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", resp.filename); // Force download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        console.error("Error: PDF not generated");
      }
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setDownloadingId(null); // Re-enable the button
    }
  };

  // const downloadPDF = async () => {
  //   setIsPdfDownloadLoading(true);
  //   const { jsPDF } = await import("jspdf");
  //   const html2canvas = (await import("html2canvas")).default;

  //   const element = document.getElementById("pdf-content");

  //   // 🔥 STORE ORIGINAL STYLES
  //   const original = {
  //     height: element.style.height,
  //     maxHeight: element.style.maxHeight,
  //     overflow: element.style.overflow
  //   };

  //   // 🔥 EXPAND FULL CONTENT
  //   element.style.height = "auto";
  //   element.style.maxHeight = "none";
  //   element.style.overflow = "visible";

  //   element.querySelectorAll("a").forEach((el) => {
  //     el.style.transform = "none";
  //   });

  //   // 🔥 hide elements before capture
  //   const ignored = element.querySelectorAll("[data-pdf-ignore]");

  //   ignored.forEach((el) => {
  //     el.dataset.prevDisplay = el.style.display; // store
  //     el.style.display = "none";
  //   });

  //   const expandSections = element.querySelectorAll("[data-pdf-expand]");

  //   const expandRestore = [];

  //   expandSections.forEach((el) => {
  //     expandRestore.push({
  //       el,
  //       height: el.style.height,
  //       overflow: el.style.overflow,
  //       maxHeight: el.style.maxHeight
  //     });

  //     const fullHeight = el.scrollHeight;

  //     el.style.overflow = "visible";
  //     el.style.maxHeight = "unset";
  //     el.style.height = fullHeight + "px";

  //     el.querySelectorAll("*").forEach((child) => {
  //       if (child?.style) {
  //         child.style.maxHeight = "unset";
  //         child.style.overflow = "visible";
  //       }
  //     });
  //   });

  //   // 🔥 CAPTURE FULL UI
  //   const canvas = await html2canvas(element, {
  //     scale: 2,
  //     useCORS: true,
  //     scrollY: -window.scrollY,
  //     backgroundColor: "#ffffff"
  //   });

  //   expandRestore.forEach(({ el, height, overflow, maxHeight }) => {
  //     el.style.height = height || "";
  //     el.style.overflow = overflow || "";
  //     el.style.maxHeight = maxHeight || "";
  //   });

  //   // 🔥 restore elements after capture
  //   ignored.forEach((el) => {
  //     el.style.display = el.dataset.prevDisplay || "";
  //   });

  //   // 🔥 RESTORE STYLES
  //   element.style.height = original.height;
  //   element.style.maxHeight = original.maxHeight;
  //   element.style.overflow = original.overflow;

  //   const pdf = new jsPDF("p", "mm", "a4");

  //   const imgWidth = 210;
  //   const pageHeight = 295;

  //   const canvasWidth = canvas.width;
  //   const canvasHeight = canvas.height;

  //   const pageHeightPx = (canvasWidth * pageHeight) / imgWidth;

  //   let renderedHeight = 0;

  //   let pageIndex = 0;

  //   const sections = element.querySelectorAll("[data-pdf-section]");
  //   const parentRect = element.getBoundingClientRect();

  //   const sectionPositions = Array.from(sections).map((section) => {
  //     const rect = section.getBoundingClientRect();

  //     return {
  //       top: (rect.top - parentRect.top + element.scrollTop) * (canvasWidth / element.offsetWidth),
  //       height: rect.height * (canvasWidth / element.offsetWidth)
  //     };
  //   });

  //   while (renderedHeight < canvasHeight) {
  //     const remainingHeight = canvasHeight - renderedHeight;

  //     let sliceHeight = Math.min(pageHeightPx, remainingHeight);

  //     // 🔥 prevent cutting inside section
  //     for (let sec of sectionPositions) {
  //       const secStart = sec.top;
  //       const secEnd = sec.top + sec.height;

  //       // if slice is cutting this section → adjust
  //       if (
  //         renderedHeight < secEnd &&
  //         renderedHeight + sliceHeight > secStart &&
  //         renderedHeight + sliceHeight < secEnd
  //       ) {
  //         sliceHeight = secStart - renderedHeight;
  //         break;
  //       }
  //     }

  //     if (sliceHeight <= 0) {
  //       renderedHeight += 10; // skip broken slice
  //       continue;
  //     }

  //     const pageCanvas = document.createElement("canvas");
  //     const ctx = pageCanvas.getContext("2d");

  //     pageCanvas.width = canvasWidth;
  //     pageCanvas.height = sliceHeight;

  //     ctx.drawImage(
  //       canvas,
  //       0,
  //       renderedHeight,
  //       canvasWidth,
  //       sliceHeight,
  //       0,
  //       0,
  //       canvasWidth,
  //       sliceHeight
  //     );

  //     const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);

  //     if (pageIndex > 0) {
  //       pdf.addPage();
  //     }

  //     // 🔥 ALWAYS draw background (FIRST + ALL pages)
  //     pdf.setFillColor(242, 247, 255);
  //     pdf.rect(0, 0, 210, 297, "F");

  //     const pdfHeight = (sliceHeight * imgWidth) / canvasWidth;

  //     if (!imgData || imgData.length < 1000) {
  //       renderedHeight += sliceHeight;
  //       continue;
  //     }

  //     pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, pdfHeight, undefined, "FAST");
  //     const links = element.querySelectorAll("a");
  //     const parentRect = element.getBoundingClientRect();
  //     const ratio = imgWidth / canvasWidth;

  //     links.forEach((link) => {
  //       const rect = link.getBoundingClientRect();

  //       const top = rect.top - parentRect.top + element.scrollTop;
  //       const left = rect.left - parentRect.left + element.scrollLeft;

  //       const x = left * ratio;
  //       const y = top * ratio;
  //       const w = rect.width * ratio;
  //       const h = rect.height * ratio;

  //       // 👉 only add if link belongs to THIS page
  //       if (y >= renderedHeight * ratio && y < (renderedHeight + sliceHeight) * ratio) {
  //         const yInPage = y - renderedHeight * ratio;

  //         pdf.link(x, yInPage, w, h, {
  //           url: link.href
  //         });
  //       }
  //     });

  //     renderedHeight += sliceHeight;
  //     pageIndex++;
  //   }

  //   pdf.save(`${memberProfileData?.m_name}.pdf`);
  //   setIsPdfDownloadLoading(false);
  // };

  return (
    <Dialog
      open={memberProfileModal}
      onClose={toggleMemberProfileModal}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: "16px",
          maxHeight: "90vh",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
        }
      }}
    >
      {!isLoading ? (
        <>
          <HeaderSection
            sx={{
              py: 2,
              px: 2
            }}
          >
            {testimonialsHistoryTracker?.length > 1 && (
              <Tooltip title="Back">
                <IconButton
                  onClick={() => {
                    GetMemberProfile(
                      testimonialsHistoryTracker[testimonialsHistoryTracker?.length - 2]
                    );
                    setTestimonialsHistoryTracker((prev) => prev.slice(0, -1));
                  }}
                  sx={{
                    position: "absolute",
                    right: 90,
                    top: 15,
                    color: "#fff",
                    backgroundColor: "#32abe4",
                    boxShadow: "0 6px 18px rgba(50, 171, 228, 0.3)",
                    "&:hover": {
                      backgroundColor: "#32abe4",
                      boxShadow: "0 8px 22px rgba(50, 171, 228, 0.4)"
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* <Tooltip title="Download PDF">
              <IconButton
                // onClick={downloadPDF}
                disabled={isPdfDownloadLoading}
                sx={{
                  position: "absolute",
                  right: 70,
                  top: 15,
                  color: "#fff",
                  backgroundColor: "#2e7d32",
                  boxShadow: "0 6px 18px rgba(46, 125, 50, 0.3)",
                  "&:hover": {
                    backgroundColor: "#1b5e20",
                    boxShadow: "0 8px 22px rgba(46, 125, 50, 0.4)"
                  },
                  width: 40,
                  height: 40
                }}
              >
                {isPdfDownloadLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </Tooltip> */}
            {/* Back Button */}
            <Tooltip title="Close">
              <IconButton
                onClick={toggleMemberProfileModal}
                sx={{
                  position: "absolute",
                  right: 15,
                  top: 15,
                  color: "#fff",
                  backgroundColor: "#d32f2f",
                  boxShadow: "0 6px 18px rgba(211, 47, 47, 0.3)",
                  "&:hover": {
                    backgroundColor: "#b71c1c",
                    boxShadow: "0 8px 22px rgba(211, 47, 47, 0.4)"
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>

            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={`${Routes.API_HOSTNAME}images/member/${memberProfileData?.photo}`}
                    alt={memberProfileData?.m_name}
                    onClick={() => setOpenImage(true)}
                    sx={{
                      width: 90,
                      height: 90,
                      border: "3px solid rgba(255,255,255,0.3)"
                    }}
                  />

                  {memberProfileData?.m_verify_status === 1 && (
                    <Box
                      component="img"
                      src="/static/icons/verified.png"
                      alt="Verified"
                      sx={{
                        width: 35,
                        height: 35,
                        position: "absolute",
                        top: 15,
                        right: 3,
                        transform: "translate(30%, -30%)"
                      }}
                    />
                  )}

                  {memberProfileData?.m_verify_status === 2 && (
                    <Box
                      component="img"
                      src="/static/icons/verified_blue.png"
                      alt="Verified"
                      sx={{
                        width: 35,
                        height: 35,
                        position: "absolute",
                        top: 15,
                        right: 3,
                        transform: "translate(30%, -30%)"
                      }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#fff" }}>
                  {memberProfileData?.m_name}
                </Typography>

                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  {memberProfileData?.city_name}
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  {memberProfileData?.business_category}
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  {memberProfileData?.business_name}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    label={memberProfileData?.current_status}
                    size="small"
                    sx={{
                      backgroundColor:
                        memberProfileData?.status === 1
                          ? "rgba(22,163,74,0.2)"
                          : "rgba(220,38,38,0.2)",
                      color: memberProfileData?.status === 1 ? "#4ade80" : "#f87171",
                      fontWeight: 600
                    }}
                  />

                  {/* <Chip
                label={`ID: ${memberProfileData?.m_id}`}
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff"
                }}
              /> */}
                </Stack>
              </Grid>
            </Grid>
          </HeaderSection>

          <DialogContent
            id="pdf-content"
            sx={{
              p: 2,
              backgroundColor: "#f4f7fb",
              overflowY: "auto",
              maxHeight: "calc(90vh - 180px)"
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: "#1c4766",
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Personal Information
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("personalInformation")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.personalInformation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.personalInformation && (
                    <Grid container spacing={3}>
                      {personalInfoItems.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              p: 0.5,
                              borderRadius: 1,
                              transition: "background-color 0.2s",
                              "&:hover": {
                                bgcolor: "#f5f5f5"
                              }
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1c4766",
                                p: 1.5,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.2rem"
                              }}
                            >
                              {item.icon}
                            </Box>

                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  letterSpacing: 0.5
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1c4766",
                                  fontSize: "0.9rem",
                                  wordBreak: "break-word"
                                }}
                              >
                                {item.value || "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: "#1c4766",
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Business Information
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("businessInformation")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.businessInformation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  {sectionOpen.businessInformation && (
                    <Grid container spacing={3}>
                      {businessInfoItems.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              p: 0.5,
                              borderRadius: 1,
                              transition: "background-color 0.2s",
                              "&:hover": {
                                bgcolor: "#f5f5f5"
                              }
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: "#e3f2fd",

                                color: "#1c4766",
                                p: 1.5,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.2rem"
                              }}
                            >
                              {item.icon}
                            </Box>

                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6b7280",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  letterSpacing: 0.5
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1c4766",
                                  fontSize: "0.9rem",
                                  wordBreak: "break-word"
                                }}
                              >
                                {item.value || "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Social Media Profiles ({memberSocialMediaProfileData?.length || 0})
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("socialMediaProfiles")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.socialMediaProfiles ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.socialMediaProfiles && (
                    <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                      {memberSocialMediaProfileData?.length > 0 ? (
                        memberSocialMediaProfileData.map((item, index) => (
                          <IconButton
                            key={index}
                            component="a"
                            href={formatUrl(item.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: "14px",
                              background: item.bg,
                              color: "#fff",
                              boxShadow: "0 6px 15px rgba(0,0,0,0.08)",
                              transition: "all 0.25s ease",
                              "&:hover": {
                                background: item.bg,
                                transform: "translateY(-4px)",
                                boxShadow: "0 10px 20px rgba(0,0,0,0.15)"
                              }
                            }}
                          >
                            {item.icon}
                          </IconButton>
                        ))
                      ) : (
                        <Box sx={{ width: "100%", textAlign: "center", py: 1 }}>
                          <Typography color="text.secondary">
                            No Social Media Profiles Yet.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Achievements (Pins) (
                      {isPinsLoading ? "..." : displayAchievements.length}
                      )
                    </Typography>
                    <IconButton onClick={() => handleToggle("achievements")} sx={{ color: "#fff" }}>
                      {sectionOpen.achievements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.achievements && (
                    <>
                      {isPinsLoading ? (
                        <AchievementsSkeleton />
                      ) : displayAchievements.length > 0 ? (
                        <Grid container spacing={2}>
                          {displayAchievements.map((item, index) => (
                            <AchievementCard key={item.a_p_id ?? index} item={item} memberProfileData={memberProfileData} onSelect={handleSelectPin} />
                          ))}
                        </Grid>
                      ) : (
                        <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                          <Typography color="text.secondary">No Achievements Earned Yet.</Typography>
                        </Box>
                      )}
                    </>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12} data-pdf-ignore>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      V2V Sheet Status
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("engagementStatus")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.engagementStatus ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.engagementStatus && (
                    <>
                      <Typography variant="caption">V2V Sheet Status</Typography>
                      <Box mt={1}>
                        <Chip
                          label={memberProfileData?.v2v_sheet_status === 1 ? "Filled" : "Pending"}
                          color={memberProfileData?.v2v_sheet_status === 1 ? "success" : "error"}
                        />

                        {memberProfileData?.v2v_sheet_status === 1 && (
                          <>
                            <Tooltip title="View V2V Sheet">
                              <IconButton
                                onClick={() => {
                                  getV2VSheetData(memberProfileData?.m_id);
                                }}
                              >
                                <RemoveRedEye color="info" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download V2V Sheet">
                              <IconButton
                                onClick={() => {
                                  downloadV2VSheetData(memberProfileData?.m_id);
                                }}
                                disabled={downloadingId === memberProfileData?.m_id}
                              >
                                {downloadingId === memberProfileData?.m_id ? (
                                  <CircularProgress size={24} color="success" />
                                ) : (
                                  <DownloadOutlined color="success" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Testimonials ({memberProfileData?.testimonial?.length || 0})
                    </Typography>
                    <IconButton onClick={() => handleToggle("testimonials")} sx={{ color: "#fff" }}>
                      {sectionOpen.testimonials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  {sectionOpen.testimonials && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12}>
                        {memberProfileData?.testimonial?.length > 0 ? (
                          <Grid container spacing={2}>
                            {memberProfileData.testimonial.map((item, i) => (
                              <Grid item xs={12} sm={6} key={i}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    p: 2,
                                    pl: 2.5,
                                    borderRadius: 3,
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                    transition: "all 0.25s ease",
                                    overflow: "hidden",
                                    "&:hover": {
                                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                      transform: "translateY(-4px)"
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 5,
                                      background: "linear-gradient(180deg, #2563eb, #7c3aed)"
                                    }}
                                  />

                                  <Box sx={{ position: "relative", flexShrink: 0, ml: 1 }}>
                                    <Avatar
                                      src={`${Routes.API_HOSTNAME}images/member/${item.photo}`}
                                      alt={item.m_name}
                                      onClick={() => {
                                        setTestimonialsHistoryTracker((prev) => [
                                          ...prev,
                                          item.m_id
                                        ]);
                                        setMemberProfileId(item.m_id);
                                      }}
                                      sx={{
                                        width: 55,
                                        height: 55,
                                        cursor: "pointer"
                                      }}
                                    />

                                    {item.m_verify_status === 2 && (
                                      <Box
                                        component="img"
                                        src="/static/icons/verified_blue.png"
                                        alt="Verified"
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          position: "absolute",
                                          top: -1,
                                          right: 3
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ flex: 1 }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {item.m_name}
                                      </Typography>

                                      <Typography variant="caption" color="text.secondary">
                                        {item.date}
                                      </Typography>
                                    </Stack>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mt: 1,
                                        color: "#4b5563",
                                        lineHeight: 1.6
                                      }}
                                    >
                                      {item.comment}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Box sx={{ width: "100%", textAlign: "center", py: 1 }}>
                            <Typography color="text.secondary">
                              No testimonials received.
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Given Testimonials ({memberProfileData?.testimonial_given?.length || 0})
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("givenTestimonials")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.givenTestimonials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.givenTestimonials && (
                    <Grid container spacing={3} data-pdf-section>
                      <Grid item xs={12} md={12}>
                        {memberProfileData?.testimonial_given?.length > 0 ? (
                          <Grid container spacing={2}>
                            {memberProfileData.testimonial_given.map((item, i) => (
                              <Grid item xs={12} sm={6} key={i}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    p: 2,
                                    pl: 2.5,
                                    borderRadius: 3,
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                    transition: "all 0.25s ease",
                                    overflow: "hidden",
                                    "&:hover": {
                                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                      transform: "translateY(-4px)"
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 5,
                                      background: "linear-gradient(180deg, #2563eb, #7c3aed)"
                                    }}
                                  />

                                  <Box sx={{ position: "relative", flexShrink: 0, ml: 1 }}>
                                    <Avatar
                                      src={`${Routes.API_HOSTNAME}images/member/${item.photo}`}
                                      alt={item.m_name}
                                      onClick={() => {
                                        setTestimonialsHistoryTracker((prev) => [
                                          ...prev,
                                          item.m_id
                                        ]);
                                        setMemberProfileId(item.m_id);
                                      }}
                                      sx={{
                                        width: 55,
                                        height: 55,
                                        cursor: "pointer"
                                      }}
                                    />

                                    {item.m_verify_status === 2 && (
                                      <Box
                                        component="img"
                                        src="/static/icons/verified_blue.png"
                                        alt="Verified"
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          position: "absolute",
                                          top: -1,
                                          right: 3
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ flex: 1 }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {item.m_name}
                                      </Typography>

                                      <Typography variant="caption" color="text.secondary">
                                        {item.date}
                                      </Typography>
                                    </Stack>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mt: 1,
                                        color: "#4b5563",
                                        lineHeight: 1.6
                                      }}
                                    >
                                      {item.comment}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Box sx={{ width: "100%", textAlign: "center", py: 1 }}>
                            <Typography color="text.secondary">
                              No given testimonials received.
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Connected & Blocked Members
                    </Typography>

                    <IconButton
                      onClick={() => handleToggle("connectedBlockedMembers")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.connectedBlockedMembers ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  {sectionOpen.connectedBlockedMembers && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12}>
                        <div style={{ padding: "10px" }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Card
                                style={{
                                  padding: "10px",
                                  minHeight: 300,
                                  border: "1px solid #c8e6c9"
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  gutterBottom
                                  style={{
                                    background: "#e8f5e9",
                                    color: "#2e7d32",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    textAlign: "center"
                                  }}
                                >
                                  Connected Members (
                                  {memberProfileData?.connected_member?.length || 0})
                                </Typography>
                                <div
                                  data-pdf-expand
                                  style={{ maxHeight: 260, overflow: "auto", paddingLeft: "10px" }}
                                >
                                  {memberProfileData?.connected_member &&
                                    memberProfileData?.connected_member.length > 0 ? (
                                    memberProfileData?.connected_member.map((item, idx) => (
                                      <div
                                        key={`connected-${idx}`}
                                        style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}
                                      >
                                        <div style={{ fontWeight: 600 }}>
                                          {item?.m_name} ({item.chpt_name})
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <Box
                                      display="flex"
                                      justifyContent="center"
                                      alignItems="center"
                                      height="100%"
                                    >
                                      <Typography color="text.secondary">
                                        No Connected Members
                                      </Typography>
                                    </Box>
                                  )}
                                </div>
                              </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Card
                                style={{
                                  padding: "10px",
                                  minHeight: 300,
                                  border: "1px solid #ffcdd2"
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  gutterBottom
                                  className="text-center"
                                  style={{
                                    background: "#ffebee",
                                    color: "#c62828",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    textAlign: "center"
                                  }}
                                >
                                  Blocked Members ({memberProfileData?.blocked_member?.length || 0})
                                </Typography>
                                <div
                                  style={{ maxHeight: 260, overflow: "auto", paddingLeft: "10px" }}
                                >
                                  {memberProfileData?.blocked_member &&
                                    memberProfileData?.blocked_member.length > 0 ? (
                                    memberProfileData?.blocked_member.map((item, idx) => (
                                      <div
                                        key={`blocked-${idx}`}
                                        style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}
                                      >
                                        <div style={{ fontWeight: 600 }}>
                                          {item?.m_name} ({item.chpt_name})
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <Box
                                      sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center"
                                      }}
                                    >
                                      <Typography color="text.secondary">
                                        No Blocked Members
                                      </Typography>
                                    </Box>
                                  )}
                                </div>
                              </Card>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12} data-pdf-ignore>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Fees History
                    </Typography>
                    <IconButton onClick={() => handleToggle("feesHistory")} sx={{ color: "#fff" }}>
                      {sectionOpen.feesHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.feesHistory && (
                    <Grid container spacing={2}>
                      {memberProfileData?.fees_history?.length > 0 ? (
                        memberProfileData.fees_history.map((item, i) => (
                          <Grid item xs={12} sm={6} key={i}>
                            <Box
                              sx={{
                                position: "relative",
                                p: 2,
                                pl: 2.5,
                                borderRadius: 3,
                                backgroundColor: "#ffffff",
                                border: "1px solid #e5e7eb",
                                display: "flex",
                                gap: 1.5,
                                alignItems: "flex-start",
                                transition: "all 0.25s ease",
                                overflow: "hidden",
                                "&:hover": {
                                  boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                  transform: "translateY(-4px)"
                                }
                              }}
                            >
                              {/* Left Gradient Line */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: 5,
                                  background: "linear-gradient(180deg, #16a34a, #22c55e)"
                                }}
                              />

                              {/* Avatar */}
                              <Box sx={{ flexShrink: 0, ml: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 55,
                                    height: 55,
                                    backgroundColor: "#e8f5e9",
                                    color: "#2e7d32",
                                    fontWeight: 700
                                  }}
                                >
                                  ₹
                                </Avatar>
                              </Box>

                              {/* Content */}
                              <Box sx={{ flex: 1 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {item.m_name}
                                  </Typography>

                                  <Typography variant="caption" color="text.secondary">
                                    {item.date}
                                  </Typography>
                                </Stack>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 1,
                                    color: "#4b5563"
                                  }}
                                >
                                  Member Fee: ₹ {item.member_fee}
                                </Typography>

                                {/* <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mt: 0.5,
                                    fontWeight: 700,
                                    color: "#16a34a"
                                  }}
                                >
                                  Amount Paid: ₹ {item.amount}
                                </Typography> */}
                              </Box>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Box sx={{ width: "100%", textAlign: "center", py: 1 }}>
                          <Typography color="text.secondary">No Fees History Available.</Typography>
                        </Box>
                      )}
                    </Grid>
                  )}
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard>
                  <Box
                    sx={{
                      backgroundColor: THEME_COLOR,
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      margin: "-24px -24px 24px -24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Inducted Member ({memberProfileData?.inducted_member?.length || 0})
                    </Typography>
                    <IconButton
                      onClick={() => handleToggle("inductedMember")}
                      sx={{ color: "#fff" }}
                    >
                      {sectionOpen.inductedMember ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {sectionOpen.inductedMember && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12}>
                        {memberProfileData?.inducted_member?.length > 0 ? (
                          <Grid container spacing={2}>
                            {memberProfileData.inducted_member.map((item, i) => (
                              <Grid item xs={12} sm={6} key={i}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    p: 2,
                                    pl: 2.5,
                                    borderRadius: 3,
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                    transition: "all 0.25s ease",
                                    overflow: "hidden",
                                    "&:hover": {
                                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                      transform: "translateY(-4px)"
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 5,
                                      background: "linear-gradient(180deg, #2563eb, #7c3aed)"
                                    }}
                                  />

                                  <Box
                                    sx={{
                                      position: "relative",
                                      flexShrink: 0,
                                      ml: 1,
                                      display: "flex",
                                      alignItems: "center"
                                    }}
                                  >
                                    <Avatar
                                      src={`${Routes.API_HOSTNAME}images/member/${item.photo}`}
                                      alt={item.m_name}
                                      onClick={() => {
                                        setTestimonialsHistoryTracker((prev) => [
                                          ...prev,
                                          item.m_id
                                        ]);
                                        setMemberProfileId(item.m_id);
                                      }}
                                      sx={{
                                        width: 55,
                                        height: 55,
                                        cursor: "pointer"
                                      }}
                                    />

                                    {item.m_verify_status === 2 && (
                                      <Box
                                        component="img"
                                        src="/static/icons/verified_blue.png"
                                        alt="Verified"
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          position: "absolute",
                                          top: -1,
                                          right: 3
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ flex: 1 }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {item?.m_name}
                                      </Typography>

                                      <Typography variant="caption" color="text.secondary">
                                        {item?.joining_date}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="subtitle2">{item?.city_name}</Typography>
                                    <Typography variant="subtitle2">
                                      {item?.business_category}
                                    </Typography>
                                    <Typography variant="subtitle2">
                                      {item?.business_name}
                                    </Typography>
                                    <Typography variant="subtitle2">{item?.mobile_no}</Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Box sx={{ width: "100%", textAlign: "center", py: 1 }}>
                            <Typography color="text.secondary">No Inducted Members Yet.</Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          </DialogContent>
          <Dialog open={openImage} onClose={() => setOpenImage(false)} maxWidth="md">
            <DialogContent sx={{ position: "relative", p: 0 }}>
              {/* Close Button */}
              <IconButton
                onClick={() => setOpenImage(false)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" }
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Large Image */}
              <img
                src={`${Routes.API_HOSTNAME}images/member/${memberProfileData?.photo}`}
                alt="Profile Large"
                style={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain"
                }}
              />
            </DialogContent>
          </Dialog>

          {selectedPinData && (
            <PinReportModal
              open={pinReportOpen}
              onClose={() => setPinReportOpen(false)}
              rowData={selectedPinData}
              fromProfile={true}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            height: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CustomLoader />
        </Box>
      )}
    </Dialog>
  );
}
